import classNames from 'clsx'
import { type ReactNode, useEffect, useState, type VFC } from 'react'
import { connectStateResults } from 'react-instantsearch-dom'

// @ts-ignore
import { checkAndGetUserZipCode } from 'shared/experiments/components/SerpNowPush/helpers'
import nowLogo from 'shared/experiments/components/SerpNowPush/now_logo.svg'
import NowCountdown from 'shared/experiments/components/SerpNowPush/NowCountdown'
import SerpNowPushModal from 'shared/experiments/components/SerpNowPush/SerpNowPushModal'
import ToggleButton from 'shared/experiments/components/SerpNowPush/ToggleButton'
import { EXPERIMENT_VARIATION, SERP_NOW_PUSH } from 'shared/experiments/consts'
import { type SearchState } from 'types/search'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import ModalShowButton from 'views/components/molecules/ModalShowButton'
import { shouldBeShippedToday } from 'views/components/organisms/NowCountdown/helpers'
import Experiment from 'views/components/utils/optimizely/Experiment'
import Variant from 'views/components/utils/optimizely/Variant'

import './SerpNowPushWidget.scss'

const NOW_FILTER_CHECKBOX_ELEMENT_SELECTOR = '#now-refinement-control-checkbox'

type SerpNowPushWidgetProps = {
  searchState: SearchState
}

const SerpNowPushWidget: VFC<SerpNowPushWidgetProps> = ({ searchState }) => {
  const [isNowAvailable, setIsNowAvailable] = useState(false)
  const [zipCodeToCheck, setZipCodeToCheck] = useState('')
  const nowState = searchState?.toggle?.nowProductGroup
  const [isNowToggled, setIsNowToggled] = useState(Boolean(nowState))

  const handleNowToggle = (value: boolean) => {
    const nowToggleCheckBoxEl: HTMLInputElement | null = document.querySelector(
      NOW_FILTER_CHECKBOX_ELEMENT_SELECTOR
    )
    if (nowToggleCheckBoxEl?.checked !== value) {
      nowToggleCheckBoxEl?.click()
    }
    setIsNowToggled(value)
  }

  useEffect(() => {
    setIsNowToggled(Boolean(nowState))
  }, [nowState])

  useEffect(() => {
    checkAndGetUserZipCode()
      .then(defaultZipCode => {
        if (defaultZipCode) {
          setZipCodeToCheck(defaultZipCode)
        }
      })
      .catch(() => {
        setIsNowAvailable(false)
      })
  }, [])

  const deliveryDay = shouldBeShippedToday() ? 'HEUTE' : 'MORGEN'

  let description: ReactNode = `Geben Sie Ihre Postleitzahl ein um zu prüfen, ob unsere Lieferung ${deliveryDay} in Ihrem Liefergebiet verfügbar ist.`

  if (zipCodeToCheck) {
    description = isNowAvailable ? (
      <>
        NOW! verfügbare Produkte innerhalb <NowCountdown /> bestellen und
        Lieferung noch {deliveryDay} erhalten.
      </>
    ) : (
      'Wir arbeiten mit Hochdruck daran, unsere NOW! Lieferung auch für Ihre PLZ anbieten zu können.'
    )
  }

  return (
    <Experiment name={SERP_NOW_PUSH}>
      <Variant name={EXPERIMENT_VARIATION.DEFAULT}>{() => null}</Variant>
      <Variant name={EXPERIMENT_VARIATION.V2}>
        {() => (
          <>
            <SerpNowPushModal
              setIsNowAvailable={setIsNowAvailable}
              setZipCodeToCheck={setZipCodeToCheck}
              zipCodeToCheck={zipCodeToCheck}
              deliveryDay={deliveryDay}
            />

            <div className="e-SerpNowPushWidget">
              <div
                className={classNames(
                  'u-margin-large--top u-padding-large u-padding@small-tiny',
                  {
                    'l-flex@medium': !zipCodeToCheck,
                  }
                )}
              >
                <div className="l-flex">
                  <div className="l-flex__start">
                    <img
                      className="o-SerpNowPushWidget--img"
                      src={nowLogo}
                      alt="now-logo"
                    />
                  </div>
                  <div className="u-padding-large--left@medium u-padding-small--left@small-tiny">
                    <h3 className="u-margin-xx-small--bottom">
                      Lieferung {deliveryDay} mit NOW!{' '}
                      <span
                        className={classNames({ 'u-hidden': !zipCodeToCheck })}
                      >
                        in{' '}
                        <ModalShowButton
                          onClick={null}
                          component={ButtonAsLink}
                          modalId="SerpNowPushModal"
                          className="u-color--dark"
                          underlined
                        >
                          {zipCodeToCheck}
                        </ModalShowButton>{' '}
                        {isNowAvailable ? 'möglich' : 'aktuell nicht möglich'}
                      </span>
                    </h3>
                    <p className="u-no-margin u-margin--top@small-tiny o-SerpNowPushWidget--desc">
                      {description}
                    </p>
                  </div>
                </div>
                {!zipCodeToCheck && (
                  <div className="u-font-weight--bold u-no-wrap u-padding--left@medium u-padding-small--top@small-tiny u-text-align--left@small-tiny">
                    <ModalShowButton
                      onClick={null}
                      component={ButtonAsLink}
                      modalId="SerpNowPushModal"
                    >
                      Verfügbarkeit prüfen
                    </ModalShowButton>
                  </div>
                )}
              </div>

              {zipCodeToCheck && isNowAvailable ? (
                <div
                  className={
                    'l-flex u-background-color--greySky u-color--now ' +
                    'u-font-weight--bold u-padding-small--ends u-padding-large--sides ' +
                    'u-border-radius--bottom-right u-border-radius--bottom-left'
                  }
                >
                  <ToggleButton
                    onChange={handleNowToggle}
                    value={isNowToggled}
                    name="now-toggle"
                  />{' '}
                  Nur NOW! Produkte anzeigen
                </div>
              ) : null}
            </div>
          </>
        )}
      </Variant>
    </Experiment>
  )
}

// @ts-ignore
export default connectStateResults(SerpNowPushWidget)
